import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useStrings, useLanguage } from '../LanguageContext';
import PrivacyNotice from '../components/PrivacyNotice';
import { LEGAL } from '../constants/legal';
import './SitePolicies.css';

// /policies, plus /terms, /privacy and /copyright, which open at their section.
const SECTION_FOR_PATH = { '/terms': 'terms', '/privacy': 'privacy', '/copyright': 'copyright' };

const SitePolicies = () => {
  const strings = useStrings();
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const id = SECTION_FOR_PATH[location.pathname] || (location.hash ? location.hash.slice(1) : '');
    if (!id) return;
    const el = document.getElementById(id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, [location.pathname, location.hash]);

  return (
  <div className="policies-page">
    <div className="policies-hero">
      <h1>{strings.sitePolicies.heading}</h1>
      <hr className="policies-divider" />
    </div>

    <div className="policies-section" id="terms">
      <h2>{strings.sitePolicies.termsHeading}</h2>
      <p>{strings.sitePolicies.termsBody}</p>
      <p>{strings.sitePolicies.termsListIntro}</p>
      <ul>
        {strings.sitePolicies.termsList.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </div>

    <div className="policies-section">
      <h2>{strings.sitePolicies.contentHeading}</h2>
      <p>{strings.sitePolicies.contentIntro}</p>
      <ul>
        {strings.sitePolicies.contentRules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
      <p>{strings.sitePolicies.contentFooter}</p>
      <p><strong>{strings.sitePolicies.contentAcceptance}</strong></p>
    </div>

    <div className="policies-section" id="privacy">
      {language === 'hi' && (
        <p><em>गोपनीयता नीति नीचे अंग्रेज़ी में है, और अंग्रेज़ी संस्करण ही मान्य है। हिंदी अनुवाद के लिए हमें {LEGAL.contactEmail} पर लिखें।</em></p>
      )}
      <PrivacyNotice />
    </div>

    <div className="policies-section">
      <h2>{strings.sitePolicies.accountHeading}</h2>
      <ul>
        {strings.sitePolicies.accountRules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
    </div>

    <div className="policies-section">
      <h2>{strings.sitePolicies.ipHeading}</h2>
      <p>{strings.sitePolicies.ipBody}</p>
    </div>

    <div className="policies-section" id="copyright">
      <h2>Copyright Complaints (Notice and Takedown)</h2>
      <p>
        We respect creators' rights. If you believe content on SaatSaheli infringes your copyright, send a notice to
        our designated agent{LEGAL.dmcaAgent.name ? `, ${LEGAL.dmcaAgent.name},` : ''} at{' '}
        <a href={`mailto:${LEGAL.dmcaAgent.email}`}>{LEGAL.dmcaAgent.email}</a>
        {LEGAL.postalAddress ? ` or by post to ${LEGAL.operator}, ${LEGAL.postalAddress}` : ''}. Your notice must include:
      </p>
      <ol>
        <li>your physical or electronic signature;</li>
        <li>the copyrighted work you say is infringed;</li>
        <li>the link to the material on SaatSaheli you want removed;</li>
        <li>your name, address, phone number and email;</li>
        <li>a statement that you believe in good faith the use is not authorized by the owner, its agent or the law;</li>
        <li>a statement, under penalty of perjury, that the notice is accurate and that you are the owner or authorized to act for the owner.</li>
      </ol>
      <p>
        We act on valid notices promptly — removing or disabling the material and telling the person who posted it —
        and within 36 hours where Indian law requires. If your content was removed and you believe that was a mistake,
        you may send a counter-notice to the same address with your signature, the removed material and where it
        appeared, a statement under penalty of perjury that it was removed by mistake or misidentification, your name,
        address and phone, and your consent to the jurisdiction of the appropriate court. We may then restore it
        after 10–14 business days unless the complainant tells us they have filed a court action.
      </p>
      <p>
        <strong>Repeat infringers:</strong> we suspend or close the accounts of users who repeatedly infringe others'
        copyright. Knowingly false notices or counter-notices can create legal liability.
        {LEGAL.dmcaAgent.registrationNumber ? ` US Copyright Office DMCA agent registration: ${LEGAL.dmcaAgent.registrationNumber}.` : ''}
      </p>
    </div>

    <div className="policies-section" id="refunds">
      <h2>{strings.sitePolicies.refundHeading}</h2>
      <p>{strings.sitePolicies.refundIntro}</p>
      <h3>{strings.sitePolicies.refundDonationsHeading}</h3>
      <p>{strings.sitePolicies.refundDonationsBody}</p>
      <h3>{strings.sitePolicies.refundRecurringHeading}</h3>
      <p>{strings.sitePolicies.refundRecurringBody}</p>
      <h3>{strings.sitePolicies.refundSubscriptionsHeading}</h3>
      <p>{strings.sitePolicies.refundSubscriptionsBody}</p>
      <h3>{strings.sitePolicies.refundMarketplaceHeading}</h3>
      <p>{strings.sitePolicies.refundMarketplaceBody}</p>
      <h3>{strings.sitePolicies.refundHowHeading}</h3>
      <p>{strings.sitePolicies.refundHowBody}</p>
    </div>

    <div className="policies-section policies-buysell">
      <h2>Creator "For Sale" Listings Disclaimer</h2>
      <p>
        <strong>This section is about pictures that creators mark "For Sale" in their galleries, where the buyer and
        creator arrange the sale between themselves.</strong> It does not apply to <Link to="/marketplace/terms">Sarayu
        Shop</Link>, where {LEGAL.operator} is the seller and its own terms, returns and refunds apply.
      </p>
      <p>
        SaatSaheli, its owners, administrators, and affiliates are <strong>not responsible</strong> for any actions,
        items, transactions, disputes, or outcomes arising from creator "For Sale" listings. This includes
        but is not limited to:
      </p>
      <ul>
        <li>The quality, safety, legality, or accuracy of any listed items or descriptions</li>
        <li>The ability of sellers to sell items or buyers to pay for items</li>
        <li>Any damages, losses, or injuries resulting from marketplace transactions</li>
        <li>Any disputes between buyers and sellers</li>
        <li>The authenticity or ownership of items listed for sale</li>
        <li>Shipping, delivery, or condition of items upon receipt</li>
      </ul>
      <p>
        <strong>All transactions are conducted entirely at the risk of the buyer and seller.</strong> Both
        parties are fully responsible for their own actions, communications, and agreements. SaatSaheli does
        not mediate disputes, process payments, or guarantee any transactions.
      </p>
      <p>
        Users are encouraged to exercise caution, verify items before purchasing, meet in safe public locations
        for in-person exchanges, and never share sensitive financial information through the platform.
      </p>
      <p>
        By buying or selling through a "For Sale" listing, you acknowledge and agree to these terms. SaatSaheli reserves the
        right to remove any listing at any time without notice.
      </p>
    </div>

    <div className="policies-section">
      <h2>{strings.sitePolicies.contactHeading}</h2>
      <p>
        {strings.sitePolicies.contactBody}{' '}
        <a href={`mailto:${strings.sitePolicies.contactEmail}`} style={{ color: '#f59e0b', fontWeight: 600 }}>
          {strings.sitePolicies.contactEmail}
        </a>.
      </p>
    </div>

    <div className="policies-updated">{strings.sitePolicies.lastUpdated}</div>
  </div>
  );
};

export default SitePolicies;
